import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Radar, ReceiptText, ShieldCheck, Truck, type LucideIcon } from 'lucide-react';
import { Panel } from '../kit.js';
import { requestOtp, verifyOtp } from '../../lib/api/queries/session.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { ApiError } from '../../lib/api/errors.js';
import { useOtpChannel } from '../api.js';

/**
 * TeraAgo's sign-in — for BOTH of its consoles.
 *
 * The OTP flow is the platform's one credential — BR-1.1: a mobile number and
 * a one-time code, no password anywhere. That behaviour is lifted unchanged
 * from the flow the other product in this bundle already ships, because it is
 * correct and covered; what is different here is the frame and the words.
 *
 * ONE DOOR, TWO ROOMS. This page used to describe the dispatcher's console and
 * send everybody to it, which was accurate while that was the only console
 * behind it. It is not any more: a driver and a vehicle owner sign in through
 * the same form, and telling them they are entering an operations console is
 * telling them something that is not true about their account. So the copy
 * names both surfaces and the landing is decided by the CAPABILITY the account
 * actually holds — never by which link they followed.
 */

/** §16.1 allows three codes per 15 minutes; 30s is the courteous floor. */
const RESEND_SECONDS = 30;

/** The two rooms behind this door, and what decides which one an account gets. */
const ADMIN_CONSOLE = '/admin';
const DRIVER_CONSOLE = '/partner';
/** A fleet company's console, and where a partner with neither registers (M069). */
const COMPANY_CONSOLE = '/company';
const JOIN = '/join';

/**
 * Where this account belongs, and whether where it was HEADED is somewhere it
 * can actually go.
 *
 * `from` is the page a guard turned them away from, and it is honoured — that
 * is the whole point of remembering it. But a driver who bookmarked `/admin`,
 * or an admin sent to `/partner` by a stale link, would otherwise be handed
 * straight back to a guard for a second refusal in a row. Their own console is
 * a better answer than the same wall twice.
 *
 * An account with neither capability lands on the admin console and meets the
 * guard's "not available" there. That is deliberate: it is the one screen that
 * states, in words, that this number has nothing here — where sending them to
 * the marketplace instead would silently drop them into a different product.
 */
/**
 * Which console THIS deployment is.
 *
 * One build serves both, and a portal is chosen at build time so the same
 * source can be published twice — once at the dispatcher's address, once at the
 * driver's — instead of being forked into two codebases that then drift.
 *
 * It only decides where the ROOT of that deployment leads. It grants nothing: a
 * driver reaching the dispatcher's address still lands on their own console,
 * because capability decides what a person may open and a hostname never does.
 */
export type Portal = 'admin' | 'partner';

export function portalOfBuild(): Portal {
  /*
    Optional, and usually absent.

    When ONE address serves both consoles — which it does — capability already
    sends each person to their own, so there is nothing for this to decide. It
    exists for the day the two are published at separate addresses, and it is
    read under either prefix because everything else here answers to
    NEXT_PUBLIC_ and two names for one setting is how a setting stops working.
  */
  const declared =
    (import.meta.env['NEXT_PUBLIC_CITYLOAD_PORTAL'] as string | undefined) ??
    (import.meta.env['VITE_CITYLOAD_PORTAL'] as string | undefined);
  return declared === 'partner' ? 'partner' : 'admin';
}

export function landingFor(
  capabilities: readonly string[],
  from: string | undefined,
  portal: Portal = portalOfBuild(),
): string {
  const admin = capabilities.includes('cl_admin');
  const driver = capabilities.includes('cl_driver');
  const company = capabilities.includes('cl_company');
  /*
    The deployment's own portal is preferred, and only when the person can
    actually use it. A driver signing in at the dispatcher's address is not
    shown a wall; an admin at the driver's address is not shown a console they
    have no work in.
  */
  const preferred = portal === 'partner' ? DRIVER_CONSOLE : ADMIN_CONSOLE;
  const canUsePreferred = portal === 'partner' ? driver : admin;
  /*
    A company is its own console. On the partner portal it comes before the
    driver's, because the person who runs a fleet is there to run it; the same
    person driving one of its machines reaches /partner from the address bar.
    Somebody on the partner portal with no console yet is sent to register
    rather than shown a wall.
  */
  const home =
    portal === 'partner' && company
      ? COMPANY_CONSOLE
      : canUsePreferred
        ? preferred
        : admin
          ? ADMIN_CONSOLE
          : company
            ? COMPANY_CONSOLE
            : driver
              ? DRIVER_CONSOLE
              : portal === 'partner'
                ? JOIN
                : ADMIN_CONSOLE;
  if (from === undefined || from === '') return home;
  if (from.startsWith(ADMIN_CONSOLE) && !admin) return home;
  if (from.startsWith(DRIVER_CONSOLE) && !driver) return home;
  if (from.startsWith(COMPANY_CONSOLE) && !company) return home;
  return from;
}

/**
 * What is behind this form, said for BOTH the people who reach it.
 *
 * Each point names screens that exist — one on the dispatcher's side, one on
 * the driver's — and carries an icon those screens use, so the promise here and
 * the consoles behind it are the same product rather than a paragraph about
 * one. A driver reading this must recognise their own week in it; an admin must
 * recognise theirs.
 */
/**
 * The three things behind the door, in a line each.
 *
 * They ran to two sentences apiece and described the same feature twice over,
 * once for the team and once for a driver, which is the kind of completeness
 * that reads as a specification. A person signing in wants to recognise where
 * they are going, not to be briefed on it.
 */
const POINTS: readonly { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: Radar,
    title: 'Live dispatch',
    body: 'Every booking, from the search to the delivery.',
  },
  {
    icon: ShieldCheck,
    title: 'Pilots and prices',
    body: 'Papers checked, rates approved, in one queue.',
  },
  {
    icon: ReceiptText,
    title: 'Fares and payouts',
    body: 'What each load earned, and what has been paid.',
  },
];

export function TeraAgoLoginPage(): ReactElement {
  const onWhatsApp = useOtpChannel() === 'whatsapp';
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  /*
    A TEN-DIGIT NUMBER IS AN INDIAN NUMBER. The field used to refuse
    "9328252672" as invalid until a +91 was typed in front of it, while the
    apps and the website take ten digits. The country code is added here, so
    every number goes to the API in the one form it accepts.
  */
  const e164 = (raw: string): string => {
    const digits = raw.replace(/[\s()-]/g, '');
    if (/^[6-9][0-9]{9}$/.test(digits)) return `+91${digits}`;
    if (/^0[6-9][0-9]{9}$/.test(digits)) return `+91${digits.slice(1)}`;
    if (/^91[6-9][0-9]{9}$/.test(digits)) return `+${digits}`;
    return digits;
  };
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /* Ids rather than wrapping labels: the hint and the error are described-by,
     and both have to be announced when the field takes focus. */
  const fieldId = useId();
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  async function sendCode(): Promise<void> {
    setPending(true);
    setError(undefined);
    try {
      await requestOtp(e164(phone));
      setStep('code');
      setCooldown(RESEND_SECONDS);
    } catch (caught) {
      if (caught instanceof ApiError) {
        // Rate limiting is a normal, expected answer here, not a failure.
        setError(caught.fieldErrors()['phone_e164'] ?? caught.detail ?? caught.title);
      } else {
        setError('We could not send a code. Check the number and try again.');
      }
    } finally {
      setPending(false);
    }
  }

  async function submitCode(): Promise<void> {
    setPending(true);
    setError(undefined);
    try {
      const tokens = await verifyOtp(e164(phone), code);
      /*
        The account comes BACK from the sign-in rather than being read off the
        hook afterwards. `has()` on this render was captured before the profile
        landed, so asking it here would route every driver to the dispatcher's
        console on the strength of a stale answer — and the guard would then
        refuse them on a screen they never asked for.
      */
      const account = await signIn({
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresIn: tokens.expires_in,
      });
      const from = (location.state as { from?: string } | null)?.from;
      navigate(landingFor(account?.capabilities ?? [], from), { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(
          caught.code === 'auth.otp_expired'
            ? 'That code has expired. Send a new one.'
            : caught.code === 'auth.otp_invalid'
              ? 'That code is not right. Check it and try again.'
              : (caught.detail ?? caught.title),
        );
      } else {
        setError('We could not verify that code.');
      }
    } finally {
      setPending(false);
    }
  }

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    /*
      THE EMPTY FIELD IS ANSWERED HERE, not by a dead button.

      Posting it would fetch a 422 the person can act on no better than they
      can act on a greyed-out control. Said under the label and focused, which
      is where they have to go next anyway.
    */
    if (value.trim() === '') {
      setError(step === 'phone' ? 'Enter your mobile number.' : 'Enter the code you were sent.');
      document.getElementById(fieldId)?.focus();
      return;
    }
    void (step === 'phone' ? sendCode() : submitCode());
  }

  const onPhoneStep = step === 'phone';
  const value = onPhoneStep ? phone : code;
  /*
    DISABLED ONLY WHILE A REQUEST IS IN FLIGHT, and it used to be disabled on an
    empty field too.

    That made the FIRST thing anybody saw on this page a grey, dead-looking
    button: nothing has been typed yet, so nothing can be, and the one control
    on the screen reads as broken before the visitor has done anything wrong.
    A sign-in page's job is to invite one action.

    An empty field is handled below instead, where the problem actually is: the
    error appears under the label and the cursor goes to it. The button stays a
    button.

    In flight it genuinely must not be pressed twice: a second tap is a second
    code, and §16.1 allows three in fifteen minutes.
  */
  const blocked = pending
    ? onPhoneStep
      ? 'A code is already on its way'
      : 'That code is being checked'
    : undefined;

  return (
    <div className="cityload cl-signin">
      {/*
        The sidebar's own lockup classes, not a copy of them. The wordmark a
        signed-out admin sees and the one above the nav two seconds later are
        then the same object, and cannot drift apart.
      */}
      <section className="cl-signin__brand">
        <div className="cl-side__brand">
          <span className="cl-side__mark" aria-hidden="true">
            <Truck size={20} strokeWidth={2.2} />
          </span>
          <span className="cl-side__names">
            <strong className="cl-side__wordmark">TeraAgo</strong>
            <span className="cl-side__sub">Console sign-in</span>
          </span>
        </div>

        <h1 className="cl-signin__headline">Sign in to TeraAgo.</h1>
        {/*
          ONE LINE, and it used to be three.

          The old lede explained the whole product to somebody who has already
          decided to sign in to it: what TeraAgo moves, how it dispatches, and
          that two kinds of people use the console. Nobody reads a paragraph to
          get to a form. The three points below already say what is behind the
          door.
        */}
        <p className="cl-signin__lede">
          The operations console for TeraAgo, and the pilot console for the people who drive for it.
        </p>

        <ul className="cl-signin__points">
          {POINTS.map((point) => (
            <li key={point.title} className="cl-signin__point">
              <point.icon size={18} strokeWidth={2} aria-hidden="true" />
              <span className="cl-signin__ptext">
                <strong>{point.title}</strong>
                <span>{point.body}</span>
              </span>
            </li>
          ))}
        </ul>

        {/*
          FOUR LINES CUT TO ONE.

          It explained the capability routing in full: which number opens which
          console, and what happens to a number that holds neither. That is the
          product working correctly, not something a person needs to read
          BEFORE typing their number. They find out by signing in, which takes
          one code.
        */}
        <p className="cl-signin__scope">
          One number, one code. Your account decides which of the two opens.
        </p>
      </section>

      <main className="cl-signin__main">
        <div className="cl-signin__panel">
          {/*
            The heading follows the step. "Sign in" over a field asking for six
            digits is a heading that has stopped helping, and the code step's
            single most useful fact is WHICH number the code went to.
          */}
          <Panel title={onPhoneStep ? 'Sign in' : 'Enter your code'}>
            <form className="cl-signin__form" onSubmit={onSubmit} noValidate>
              {/*
                The phone step's line said the same thing the field, the button
                and the notice below it already say, in a sentence and a half.
                The code step's line is the one that earns its place: which
                number, and how long there is.
              */}
              <p className="cl-signin__lead">
                {onPhoneStep
                  ? 'No password. We send a one-time code.'
                  : `Sent to ${phone}. Good for ten minutes.`}
              </p>

              {/*
                TWO LINES, AND THE OLD SEVEN WERE ALSO WRONG.

                It ran to a paragraph about the server hashing the code and
                discarding the plaintext, and ended by saying "sign-in cannot be
                completed from this screen". That was true when it was written
                and is not any more: `DEV_OTP_BYPASS` means a demo code signs
                somebody straight in. So the screen was telling people the door
                was shut while they were standing in front of an open one.

                What a person needs here is one fact and one instruction. Why
                the SMS does not arrive is ours to know.
              */}
              <p className="cl-notice">
                {onWhatsApp ? (
                  <>
                    <strong>Your code comes on WhatsApp.</strong> No SMS is sent.
                  </>
                ) : (
                  <>
                    <strong>No code will reach your phone yet.</strong> Ask TeraAgo for the demo
                    code.
                  </>
                )}
              </p>

              <div className="cl-field">
                <label className="cl-field__label" htmlFor={fieldId}>
                  {onPhoneStep ? 'Mobile number' : 'Six-digit code'}
                </label>
                <input
                  id={fieldId}
                  className="cl-field__input"
                  type={onPhoneStep ? 'tel' : 'text'}
                  inputMode={onPhoneStep ? 'tel' : 'numeric'}
                  autoComplete={onPhoneStep ? 'tel' : 'one-time-code'}
                  aria-describedby={error === undefined ? hintId : `${hintId} ${errorId}`}
                  aria-invalid={error === undefined ? undefined : true}
                  value={value}
                  onChange={(event) =>
                    onPhoneStep ? setPhone(event.target.value) : setCode(event.target.value)
                  }
                />
                <span className="cl-signin__hint" id={hintId}>
                  {onPhoneStep
                    ? 'Your 10 digit mobile number, like 98765 43210.'
                    : /* The lead above already names the number. Repeating it
                         under the field is the same fact twice in 40px. */
                      'Six digits.'}
                </span>
              </div>

              {error === undefined ? null : (
                <p className="cl-signin__error" id={errorId} role="alert">
                  {error}
                </p>
              )}

              <button
                type="submit"
                className="cl-btn cl-btn--primary cl-btn--block cl-signin__submit"
                disabled={blocked !== undefined}
                aria-busy={pending}
                {...(blocked === undefined ? {} : { title: blocked })}
              >
                {onPhoneStep
                  ? pending
                    ? 'Sending code…'
                    : 'Send code'
                  : pending
                    ? 'Signing in…'
                    : 'Sign in'}
              </button>

              {onPhoneStep ? null : (
                <div className="cl-signin__meta">
                  {/*
                    The countdown is the reason, written into the label itself
                    — a disabled button that says "Resend in 21s" needs no
                    tooltip to explain itself. In flight, it does.
                  */}
                  <button
                    type="button"
                    className="cl-btn cl-btn--ghost cl-btn--sm"
                    disabled={cooldown > 0 || pending}
                    {...(pending && cooldown === 0
                      ? { title: 'Wait for the request already in flight to finish' }
                      : {})}
                    onClick={() => void sendCode()}
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                  </button>
                  <button
                    type="button"
                    className="cl-btn cl-btn--ghost cl-btn--sm"
                    onClick={() => {
                      setStep('phone');
                      setCode('');
                      setError(undefined);
                    }}
                  >
                    Change number
                  </button>
                </div>
              )}
            </form>
          </Panel>
        </div>
      </main>
    </div>
  );
}
